export type DataLayerValue = string | number | boolean | null | undefined;

export type DataLayerEventData = Record<string, DataLayerValue>;

type DataLayerWindow = Window & {
	dataLayer?: unknown[];
};

export function getDataLayerInitScriptContent(): string {
	return "window.dataLayer = window.dataLayer || [];";
}

function ensureDataLayer(): unknown[] | null {
	if (typeof window === "undefined") return null;
	const w = window as DataLayerWindow;
	w.dataLayer = w.dataLayer ?? [];
	return w.dataLayer;
}

export function pushToDataLayer(payload: Record<string, unknown>): boolean {
	const dataLayer = ensureDataLayer();
	if (!dataLayer) return false;
	dataLayer.push(payload);
	return true;
}

export function trackDataLayerEvent<TEvent extends string>(event: TEvent, data: DataLayerEventData = {}): boolean {
	return pushToDataLayer({
		event,
		...data,
	});
}

/**
 * Maps submitted form values to flat dataLayer properties. Nested values (e.g. search profile, select objects) are JSON-stringified.
 *
 * @param values - Field id → value map from the form state (same shape as submit payload).
 * @returns Key/value pairs safe for `trackDataLayerEvent` / GTM dataLayer.
 */
export function formFieldValuesToDataLayerData(values: Record<string, unknown>): DataLayerEventData {
	const out: DataLayerEventData = {};

	for (const [key, val] of Object.entries(values)) {
		if (val === undefined || (Array.isArray(val) && val.some((item) => item instanceof File))) {
			continue;
		}

		if (val === null) {
			out[key] = null;
			continue;
		}

		const t = typeof val;
		if (t === "string" || t === "number" || t === "boolean") {
			out[key] = val as string | number | boolean;
			continue;
		}

		out[key] = JSON.stringify(val);
	}

	return out;
}
